import React, { useEffect, useState, useRef } from "react";
import { ChevronLeft, ChevronRight } from 'lucide-react';

// PROFESSIONAL full-screen banners with smooth transitions
const NewHeroSections = () => {
  const [currentSection, setCurrentSection] = useState(0);
  const [showOverlay, setShowOverlay] = useState(false);
  const [overlayText, setOverlayText] = useState("");
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef(null);

  const sections = [
    { id: 0, name: 'awareness' },
    { id: 1, name: 'people', overlayWord: 'PEOPLE' },
    { id: 2, name: 'together', overlayWord: 'COMMUNITY' },
    { id: 3, name: 'environment', overlayWord: 'ENVIRONMENT' }
  ];

  const goToNextSection = () => {
    const nextIndex = (currentSection + 1) % sections.length;
    const nextSection = sections[nextIndex];
    
    if (nextSection.overlayWord) {
      setOverlayText(nextSection.overlayWord);
      setShowOverlay(true);
      
      setTimeout(() => {
        setShowOverlay(false);
        setCurrentSection(nextIndex);
        setTimeout(() => setOverlayText(""), 300);
      }, 1500);
    } else {
      setCurrentSection(nextIndex);
    }
  };

  const goToPrevSection = () => {
    const prevIndex = currentSection === 0 ? sections.length - 1 : currentSection - 1;
    setCurrentSection(prevIndex);
    setShowOverlay(false);
    setOverlayText("");
  };

  useEffect(() => {
    // Auto-transition works always, but pauses on hover
    if (!isPaused) {
      timerRef.current = setInterval(() => {
        goToNextSection();
      }, 7000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [currentSection, isPaused]);

  const goToSection = (index) => {
    setCurrentSection(index);
    setShowOverlay(false);
    setOverlayText("");
  };

  const handleMouseEnter = () => {
    setIsPaused(true);
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
  };

  return (
    <div 
      className="relative w-full" 
      style={{ height: '100vh', minHeight: '700px' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      
      <style>{`
        @keyframes fadeSection {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes overlayFade {
          0% { opacity: 0; transform: scale(0.7); }
          50% { opacity: 0.6; transform: scale(1); }
          100% { opacity: 0; transform: scale(0.85); }
        }
        
        .fade-in {
          animation: fadeSection 0.8s ease-out forwards;
        }
        
        .overlay-fade {
          animation: overlayFade 1.5s ease-out forwards;
        }
      `}</style>

      {/* Overlay text - BELOW header */}
      {showOverlay && overlayText && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ zIndex: 1, paddingTop: '120px' }}>
          <h1 className="overlay-fade text-[9rem] lg:text-[13rem] font-black text-white/40 leading-none">
            {overlayText}
          </h1>
        </div>
      )}

      {/* Content sections */}
      <div className="relative w-full h-full">
        
        {/* SECTION 1: CNESS Works - Brand & Trust Builder */}
        {currentSection === 0 && (
          <div className="fade-in absolute inset-0 bg-cover bg-center" style={{backgroundImage: 'linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url(https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80)'}}>
            <div className="container mx-auto px-6 lg:px-12 h-full flex items-center">
              <div className="max-w-3xl text-white pt-20">
                <h1 className="text-6xl lg:text-8xl font-black leading-[0.9] mb-6">
                  CNESS Works<br />
                  <span className="text-amber-400">Brand & Trust Builder</span>
                </h1>
                
                <p className="text-xl lg:text-2xl font-light leading-relaxed mb-8 max-w-2xl">
                  At CNESS, we believe authentic transformation comes from organizations that live their values every day. CNESS Works is a live, operational platform where companies onboard to validate their principles, demonstrate conscious alignment and strengthen credibility through certification and adoption of proven best practices.
                </p>

                <div className="flex flex-wrap gap-4 mb-20">
                  <a href="/certification">
                    <button className="px-10 py-4 bg-white text-gray-900 rounded-full font-semibold hover:bg-gray-100 transition-all">
                      EXPLORE THE FRAMEWORK
                    </button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SECTION 2: PEOPLE */}
        {currentSection === 1 && (
          <div className="fade-in absolute inset-0 bg-cover bg-right" style={{backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.95) 40%, rgba(255,255,255,0.1)), url(https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=1920&q=80)', backgroundPosition: '75% 30%', backgroundSize: 'cover'}}>
            <div className="container mx-auto px-6 lg:px-12 h-full flex items-center">
              <div className="max-w-2xl pt-20">
                <p className="text-2xl lg:text-3xl font-bold text-gray-900 mb-5">
                  WHERE GROWTH MEETS PURPOSE
                </p>

                <p className="text-lg text-gray-700 leading-relaxed mb-8 max-w-xl">
                  People form the soul of every conscious enterprise. Growth attains meaning only when guided by purpose, empathy, and ethical intelligence. CNESS nurtures leaders who balance vision with vulnerability — transforming not just performance, but the very definition of success within modern organizations.
                </p>

                <div className="flex flex-wrap gap-4 mb-20">
                  <a href="/mentor-partner">
                    <button className="px-10 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-full font-semibold hover:shadow-xl transition-all">
                      MEET OUR LEADERS
                    </button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SECTION 3: TOGETHER, WE RISE */}
        {currentSection === 2 && (
          <div className="fade-in absolute inset-0 bg-cover bg-center" style={{backgroundImage: 'linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=1920&q=80)'}}>
            <div className="container mx-auto px-6 lg:px-12 h-full flex items-center justify-center">
              <div className="text-center max-w-4xl text-white pt-20">
                <h2 className="text-6xl lg:text-8xl font-black leading-[0.9] mb-6 drop-shadow-2xl">
                  TOGETHER,<br />
                  <span className="text-white/95">WE RISE</span>
                </h2>
                
                <p className="text-2xl lg:text-3xl font-bold mb-5 drop-shadow-lg">
                  REAL IMPACT BEGINS WITH UPLIFTING OTHERS.
                </p>

                <p className="text-lg leading-relaxed mb-8 max-w-3xl mx-auto drop-shadow-lg bg-black/20 p-6 rounded-lg backdrop-blur-sm">
                  The future belongs to those who rise together. When purpose-driven enterprises collaborate through shared consciousness, they transcend competition to co-create ecosystems of trust, innovation, and regenerative impact. At CNESS, we believe collective elevation is the new metric of greatness.
                </p>

                <div className="flex flex-wrap gap-4 justify-center mb-20">
                  <a href="/signup">
                    <button className="px-10 py-4 bg-white text-gray-900 rounded-full font-semibold hover:bg-gray-100 transition-all shadow-xl">
                      JOIN THE MOVEMENT
                    </button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SECTION 4: ENVIRONMENT */}
        {currentSection === 3 && (
          <div className="fade-in absolute inset-0 bg-cover bg-right" style={{backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.88) 50%, rgba(255,255,255,0.1)), url(https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1920&q=80)', backgroundPosition: '70% center'}}>
            <div className="container mx-auto px-6 lg:px-12 h-full flex items-center">
              <div className="max-w-2xl pt-20">
                <h2 className="text-6xl lg:text-8xl font-black leading-none text-emerald-700 mb-6">
                  ENVIRONMENT
                </h2>
                
                <p className="text-2xl lg:text-3xl font-bold text-gray-900 mb-5">
                  SUSTAINABILITY AS A STANDARD
                </p>

                <p className="text-lg text-gray-700 leading-relaxed mb-8 max-w-xl">
                  Sustainability is not a trend — it is the standard of conscious enterprise. Environmental responsibility begins with awareness and culminates in stewardship. CNESS empowers organizations to embed ecological consciousness into strategy, ensuring every action today safeguards the generations of tomorrow.
                </p>

                <div className="flex flex-wrap gap-4 mb-20">
                  <a href="/signup">
                    <button className="px-10 py-4 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-full font-semibold hover:shadow-xl transition-all">
                      START YOUR JOURNEY
                    </button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation with Arrows and 4 Dots */}
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 flex items-center gap-4 z-20">
        {/* Left Arrow */}
        <button
          onClick={goToPrevSection}
          className="w-10 h-10 rounded-full bg-white/80 hover:bg-white flex items-center justify-center transition-all shadow-lg"
          aria-label="Previous section"
        >
          <ChevronLeft className="w-5 h-5 text-gray-900" />
        </button>

        {/* 4 Dots */}
        <div className="flex gap-3">
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => goToSection(section.id)}
              className={`rounded-full transition-all duration-300 ${
                currentSection === section.id 
                  ? 'bg-white w-10 h-3' 
                  : 'bg-white/50 hover:bg-white/75 w-3 h-3'
              }`}
              aria-label={`Go to section ${section.id + 1}`}
            />
          ))}
        </div>

        {/* Right Arrow */}
        <button
          onClick={goToNextSection}
          className="w-10 h-10 rounded-full bg-white/80 hover:bg-white flex items-center justify-center transition-all shadow-lg"
          aria-label="Next section"
        >
          <ChevronRight className="w-5 h-5 text-gray-900" />
        </button>
      </div>
    </div>
  );
};

export default NewHeroSections;